<template>
	<v-menu v-model="open" :disabled="disabled" :close-on-content-click="false" @after-enter="scrollToSelected">
		<template #activator="{ props }">
			<v-text-field
				v-bind="props"
				ref="field"
				class="time-select"
				:model-value="inputValue"
				:disabled="disabled"
				:error="error"
				variant="outlined"
				rounded="lg"
				hide-details="auto"
				aria-label="Time"
				role="combobox"
				aria-haspopup="dialog"
				append-inner-icon="icon-clock-stroke"
				@update:model-value="handleManualInput"
				@blur="resetInvalidInput" />
		</template>
		<v-card>
			<div ref="panel" class="d-flex pa-1 ga-1" role="dialog" aria-label="Choose time" @keydown.esc="field?.focus()">
				<div v-for="column in columns" :key="column.label" class="flex-1-1-0">
					<div class="text-caption text-center py-1">{{ column.label }}</div>
					<v-list :aria-label="column.label" role="listbox" class="pa-0 position-relative" max-height="240">
						<v-list-item
							v-for="option in column.options"
							:key="option"
							:value="option"
							:active="option === column.value"
							class="text-center px-3"
							color="primary"
							variant="flat"
							@click="column.select(option)">
							{{ option }}
						</v-list-item>
					</v-list>
				</div>
			</div>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
	import { computed, nextTick, ref, watch } from "vue";
	import type { VTextField } from "vuetify/components";

	// Controlled HH:mm value. The caller owns date/timezone validation and Save/Cancel.
	const model = defineModel<string>({ required: true });
	defineProps<{ disabled?: boolean; error?: boolean }>();

	const open = ref(false);
	const field = ref<InstanceType<typeof VTextField>>();
	const panel = ref<HTMLElement>();
	const hours = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
	const minutes = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));
	const periods = ["AM", "PM"];
	const hour24 = computed(() => Number(model.value.slice(0, 2)));
	const inputValue = ref("");

	const hour = computed({
		get: () => String(hour24.value % 12 || 12).padStart(2, "0"),
		set: (value: string) => setTime((Number(value) % 12) + (period.value === "PM" ? 12 : 0), minute.value)
	});
	const minute = computed({
		get: () => model.value.slice(3, 5),
		set: (value: string) => setTime(hour24.value, value)
	});
	const period = computed({
		get: () => (hour24.value < 12 ? "AM" : "PM"),
		set: (value: string) => setTime((hour24.value % 12) + (value === "PM" ? 12 : 0), minute.value)
	});
	const formattedTime = computed(() => `${hour.value}:${minute.value} ${period.value}`);

	const columns = computed(() => [
		{
			label: "Hour",
			options: hours,
			value: hour.value,
			select: (value: string) => {
				hour.value = value;
			}
		},
		{
			label: "Minute",
			options: minutes,
			value: minute.value,
			select: (value: string) => {
				minute.value = value;
			}
		},
		{
			label: "Period",
			options: periods,
			value: period.value,
			select: (value: string) => {
				period.value = value;
			}
		}
	]);

	watch(formattedTime, (value) => (inputValue.value = value), { immediate: true });

	function scrollToSelected() {
		panel.value?.querySelectorAll<HTMLElement>('[role="listbox"]').forEach((list) => {
			const selected = list.querySelector<HTMLElement>('[aria-selected="true"]');
			if (selected) list.scrollTop = selected.offsetTop;
		});
	}

	function setTime(selectedHour: number, selectedMinute: string) {
		model.value = `${String(selectedHour).padStart(2, "0")}:${selectedMinute}`;
	}

	function handleManualInput(value: string, commitWithoutPeriod = false) {
		inputValue.value = value;
		const normalizedValue = value.trim().toUpperCase();
		const match = normalizedValue.includes(":")
			? normalizedValue.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/)
			: normalizedValue.match(/^(\d{2})(\d{2})\s*(AM|PM)?$/);
		// Keep the draft untouched until its period is complete or focus leaves the field.
		// Inserting the existing AM/PM while typing makes the next characters append to it.
		if (!match || (!match[3] && !commitWithoutPeriod)) return;

		const selectedHour = Number(match[1]);
		const selectedMinute = Number(match[2]);
		if (selectedHour < 1 || selectedHour > 12 || selectedMinute > 59) return;

		const selectedPeriod = match[3] ?? period.value;
		const selectedHour24 = (selectedHour % 12) + (selectedPeriod === "PM" ? 12 : 0);
		const selectedTime = `${String(selectedHour24).padStart(2, "0")}:${String(selectedMinute).padStart(2, "0")}`;
		if (selectedTime === model.value) {
			// Let the field render the draft before normalizing an unchanged model value.
			nextTick(() => {
				if (inputValue.value === value) inputValue.value = formattedTime.value;
			});
			return;
		}

		setTime(selectedHour24, String(selectedMinute).padStart(2, "0"));
		inputValue.value = formattedTime.value;
	}

	function resetInvalidInput() {
		handleManualInput(inputValue.value, true);
		inputValue.value = formattedTime.value;
	}
</script>

<style scoped>
	.time-select:not(.v-input--disabled) :deep(input) {
		cursor: text;
	}

	.time-select:not(.v-input--disabled) :deep(.v-field__append-inner .v-icon) {
		cursor: pointer;
	}
</style>
